import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { SITE } from "@/config/site";
import { Reveal } from "./Reveal";

const PROMPTS = [
  {
    id: "performance",
    label: "Weekly performance",
    question: "How did my clinic perform this week?",
    answer:
      "Here's your week at a glance: appointments, collections and new patient leads in one view. Revenue, pending payments and chair utilisation stay updated — so you always know where your clinic stands.",
  },
  {
    id: "reviews",
    label: "Review replies",
    question: "Draft a reply to a 5-star Google review.",
    answer:
      "\u201CThank you for the kind words! We're glad your visit was comfortable, and we look forward to seeing you at your next appointment.\u201D — DIVA drafts replies you can review and post in one tap.",
  },
  {
    id: "lab",
    label: "Lab work",
    question: "What's pending in the lab this week?",
    answer:
      "You have lab work due this week for patients already on your schedule. DIVA connects lab deadlines to appointments, so nothing is forgotten on the day it matters.",
  },
];

const Diva = () => {
  const [active, setActive] = useState(0);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    setTyped("");
    const answer = PROMPTS[active].answer;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(answer.slice(0, i));
      if (i >= answer.length) clearInterval(id);
    }, 14);
    return () => clearInterval(id);
  }, [active]);

  return (
    <section id="diva" data-testid="diva-section" className="densa-grain overflow-hidden bg-brand-deep py-20 text-white sm:py-28">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
        <div>
          <Reveal>
            <p data-testid="diva-badge" className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-2 text-xs font-bold uppercase tracking-[0.25em] text-emerald-200">
              <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              Meet DIVA
            </p>
            <h2 data-testid="diva-heading" className="mt-5 text-2xl font-bold tracking-tight sm:text-3xl lg:text-4xl">
              Your AI assistant, <span className="text-emerald-300">inside your clinic.</span>
            </h2>
            <p className="mt-5 max-w-lg text-base leading-relaxed text-emerald-50/70">
              DIVA — DENSA&apos;s Dental Intelligent Virtual Assistant — reads your clinic data and gives you insights,
              assistance and actionable guidance. Ask about performance, get help with reviews and posts, and make
              informed decisions without digging through reports.
            </p>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-emerald-50/70">
              Not a separate tool. Not another app. DIVA lives where your clinic already is — inside DENSA.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <img src={SITE.assets.logoWhite} alt="DENSA logo" className="mt-10 h-8 w-auto opacity-90 sm:h-9" />
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="rounded-[1.75rem] border border-white/10 bg-white/[0.06] p-5 shadow-2xl backdrop-blur-md sm:p-7" data-testid="diva-demo-box">
            <div className="flex items-center gap-3 border-b border-white/10 pb-4">
              <img src={SITE.assets.appIcon} alt="DENSA app icon" className="h-9 w-9 rounded-xl" />
              <div>
                <p className="text-sm font-bold">DIVA</p>
                <p className="text-[11px] text-emerald-200/70">Dental Intelligent Virtual Assistant</p>
              </div>
            </div>

            <div className="mt-5 flex justify-end">
              <p className="max-w-[85%] rounded-2xl rounded-br-md bg-brand px-4 py-3 text-sm leading-relaxed text-white" data-testid="diva-prompt-bubble">
                {PROMPTS[active].question}
              </p>
            </div>

            <div className="mt-4 flex justify-start">
              <p className="min-h-[96px] max-w-[92%] rounded-2xl rounded-bl-md bg-white/10 px-4 py-3 text-sm leading-relaxed text-emerald-50" data-testid="diva-response-card">
                {typed}
                <span className="densa-caret ml-0.5 inline-block h-4 w-[2px] translate-y-0.5 bg-emerald-300" aria-hidden="true" />
              </p>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              {PROMPTS.map((p, i) => (
                <button
                  key={p.id}
                  type="button"
                  data-testid={`diva-tab-${p.id}`}
                  onClick={() => setActive(i)}
                  aria-pressed={active === i}
                  className={`min-h-[44px] rounded-full border px-4 py-2 text-xs font-semibold transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300 ${
                    active === i
                      ? "border-emerald-300 bg-emerald-300 text-brand-deep"
                      : "border-white/15 bg-white/5 text-emerald-50/80 hover:border-emerald-300/50 hover:text-white"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <p className="mt-4 text-[11px] text-emerald-100/40">Illustrative preview of the DIVA experience inside the DENSA app.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Diva;
